// Results: the one place a student lands after doing work. Two honest views:
//   - Mocks: full sittings (all 4 sections rolled up per attempt).
//   - Practice: every standalone practice set — R/L auto-scored attempts AND
//     W/S AI-scored submissions live as siblings in one chronological list,
//     filterable by skill. There is no third "Writing & speaking" tab: W/S are
//     just practice too.
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, BookOpen, Headphones, Layers, Mic, PenLine } from 'lucide-react';

import { api } from '@/lib/api';
import { mockLabel, prettyTask, statusMeta } from '@/lib/labels';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

const TABS = [
  { key: 'practice', label: 'Practice' },
  { key: 'mocks', label: 'Mocks' },
];

const SECTION_ICON = { reading: BookOpen, listening: Headphones, writing: PenLine, speaking: Mic };
const SKILL_TABS = [
  { key: 'all', label: 'All skills', Icon: Layers },
  { key: 'reading', label: 'Reading', Icon: BookOpen },
  { key: 'listening', label: 'Listening', Icon: Headphones },
  { key: 'writing', label: 'Writing', Icon: PenLine },
  { key: 'speaking', label: 'Speaking', Icon: Mic },
];

const when = (iso) => (iso ? `${iso.slice(0, 10)} · ${iso.slice(11, 16)}` : '');
const pctCls = (p) => (p >= 75 ? 'text-emerald-600' : p >= 50 ? 'text-indigo-600' : 'text-red-600');

// One shape for the unified Practice list. R/L attempts and W/S submissions
// both fold into this so the row renderer stays boringly simple.
function toPracticeRow(item, kind) {
  if (kind === 'attempt') {
    const pct = item.n_total ? Math.round((item.n_correct / item.n_total) * 100) : 0;
    return {
      kind: 'attempt',
      id: item.attempt_id,
      section: item.section,
      task_type: item.task_type,
      at: item.at,
      n_correct: item.n_correct,
      n_total: item.n_total,
      pct,
    };
  }
  return {
    kind: 'submission',
    id: item.submission_id,
    section: item.section,
    task_type: item.task_type,
    at: item.created_at,
    status: item.status,
    feedback: item.feedback,
    gate_flags: item.gate_flags,
  };
}

export default function ResultsPage() {
  const { exam } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [attempts, setAttempts] = React.useState(null);
  const [subs, setSubs] = React.useState(null);
  const [sittings, setSittings] = React.useState(null);
  const [units, setUnits] = React.useState([]);
  const [error, setError] = React.useState('');

  const tab = params.get('view') || 'practice';
  const skill = params.get('skill') || 'all';

  useEffect(() => {
    const load = async () => {
      try {
        const [a, s, u, m] = await Promise.all([
          api.myAttempts(exam), api.mySubmissions(exam), api.curriculum(exam), api.mockSittings(exam),
        ]);
        setAttempts(a.data.attempts);
        setSubs(s.data.submissions);
        setUnits(u.data.units);
        setSittings(m.data.sittings);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load your results.');
      }
    };
    load();
  }, [exam]);

  const titleFor = useMemo(() => {
    const map = {};
    units.forEach((u) => { if (u.task_type) map[u.task_type] = u.title; });
    return (tt) => map[tt] || prettyTask(tt);
  }, [units]);

  // Merge R/L attempts + standalone W/S submissions into one chronological
  // list. Anything tied to a mock sitting (mock_form != null) lives under the
  // Mocks tab instead — never mixed in here.
  const practiceRows = useMemo(() => {
    const rows = [];
    (attempts || []).filter((a) => a.kind === 'practice').forEach((a) => rows.push(toPracticeRow(a, 'attempt')));
    (subs || []).filter((s) => s.mock_form == null).forEach((s) => rows.push(toPracticeRow(s, 'submission')));
    rows.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
    return rows;
  }, [attempts, subs]);

  const skillCounts = useMemo(() => {
    const c = { all: practiceRows.length, reading: 0, listening: 0, writing: 0, speaking: 0 };
    practiceRows.forEach((r) => { if (c[r.section] !== undefined) c[r.section] += 1; });
    return c;
  }, [practiceRows]);

  const filteredPractice = useMemo(
    () => (skill === 'all' ? practiceRows : practiceRows.filter((r) => r.section === skill)),
    [practiceRows, skill]
  );

  if (error) return (
    <AppShell>
      <p className="mono-stat">{exam} · results</p>
      <h1 className="h-display mt-2 text-3xl">Couldn’t load your results.</h1>
      <p className="mt-2 text-sm text-slate-500">{error}</p>
      <button type="button" onClick={() => window.location.reload()} className="cta mt-5">Try again</button>
    </AppShell>
  );

  return (
    <AppShell>
      <p className="mono-stat">{exam} · results</p>
      <h1 className="h-display mt-2 text-3xl">How you did.</h1>
      <p className="mt-2 max-w-xl text-sm text-slate-500">
        {tab === 'mocks'
          ? 'Every full mock sitting you finished — all four sections in one card.'
          : 'Every timed practice set across the four skills. Reading and Listening are auto-scored; Writing and Speaking are AI-marked.'}
      </p>

      <div className="mt-6 flex border-b-2 border-slate-900" data-testid="results-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`results-tab-${t.key}`}
            onClick={() => setParams({ view: t.key }, { replace: true })}
            className={cn(
              'px-5 py-3 text-sm font-semibold transition-colors',
              t.key === tab ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-900'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {attempts === null || subs === null || sittings === null ? (
        <p className="py-10 text-slate-400">Loading…</p>
      ) : (
        <div className="mt-6 max-w-3xl">
          {tab === 'mocks' && (
            sittings.length === 0 ? (
              <p className="py-6 text-sm text-slate-500">No mock sittings yet. Sit a full mock and every section, reading, listening, writing, and speaking, lands here together.</p>
            ) : (
              <div className="space-y-6">
                {sittings.map((s) => {
                  const auto = {};
                  (s.auto || []).forEach((a) => { auto[a.section] = a; });
                  const resp = {};
                  (s.responses || []).forEach((r) => { (resp[r.section] = resp[r.section] || []).push(r); });
                  return (
                    <div key={`${s.form}-${s.at}`} className="border border-slate-200 bg-white" data-testid={`sitting-${s.form}-${(s.at || '').slice(0, 10)}`}>
                      <div className="flex items-baseline justify-between border-b border-slate-100 px-5 py-3.5">
                        <div>
                          <p className="font-display text-[15px] font-semibold text-slate-900">Mock {mockLabel(s.form)}</p>
                          <p className="text-xs text-slate-400">{when(s.at)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/${exam}/mock/${s.form}/review?at=${encodeURIComponent(s.at)}`)}
                          data-testid={`sitting-review-${s.form}-${(s.at || '').slice(0, 10)}`}
                          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                        >
                          Review sitting <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {['listening', 'reading', 'writing', 'speaking'].map((sec) => {
                        const a = auto[sec];
                        const rs = resp[sec] || [];
                        if (!a && rs.length === 0) return null;
                        const pct = a && a.n_total ? Math.round((a.n_correct / a.n_total) * 100) : 0;
                        return (
                          <div key={sec} className="border-t border-slate-50 px-5 py-3 first:border-t-0" data-testid={`sitting-${s.form}-${sec}`}>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{sec}</p>
                            {a && (
                              <div className="mt-1 flex items-center justify-between">
                                <span className="text-sm tabular-nums text-slate-500">{a.n_correct} / {a.n_total} correct</span>
                                <span className={cn('h-display text-lg tabular-nums', pctCls(pct))}>{pct}%</span>
                              </div>
                            )}
                            {rs.map((r) => {
                              const st = statusMeta(r.status);
                              const zeroed = r.status === 'gate_zeroed';
                              const band = !zeroed && (r.feedback?.band_estimate || r.feedback?.clb_band_estimate);
                              return (
                                <div key={r.submission_id} className="mt-1.5 flex items-center justify-between gap-3">
                                  <span className="truncate text-sm text-slate-600">{titleFor(r.task_type)}</span>
                                  <span className="flex shrink-0 items-center gap-2">
                                    {band && <span className="h-display text-base text-slate-900">{band}</span>}
                                    <span className={cn('rounded px-2 py-0.5 text-xs font-semibold', st.cls)}>{st.text}</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === 'practice' && (
            <>
              {/* Skill selector — full presentation, not tiny filter chips.
                  Icons + counts + underline for active. Sits on its own
                  block so it reads like a header for what's below, not a
                  quiet filter tucked into a corner. */}
              <div
                className="mt-4 grid grid-cols-5 overflow-hidden rounded-lg border border-slate-200 bg-white"
                data-testid="practice-skill-tabs"
              >
                {SKILL_TABS.map((c) => {
                  const active = c.key === skill;
                  const count = skillCounts[c.key] || 0;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`practice-skill-${c.key}`}
                      onClick={() => setParams({ view: 'practice', skill: c.key }, { replace: true })}
                      className={cn(
                        'group relative flex flex-col items-center justify-center gap-1.5 border-r border-slate-200 px-3 py-4 text-center transition-colors last:border-r-0',
                        active
                          ? 'bg-slate-900 text-white'
                          : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                      )}
                    >
                      <c.Icon className={cn('h-5 w-5', active ? 'text-white' : 'text-slate-400 group-hover:text-slate-900')} />
                      <span className="font-display text-[13px] font-semibold sm:text-sm">{c.label}</span>
                      <span
                        className={cn(
                          'text-[11px] font-semibold tabular-nums',
                          active ? 'text-slate-300' : 'text-slate-400'
                        )}
                      >
                        {count} {count === 1 ? 'attempt' : 'attempts'}
                      </span>
                    </button>
                  );
                })}
              </div>

              {filteredPractice.length === 0 ? (
                <p className="mt-6 py-6 text-sm text-slate-500">
                  {skill === 'all'
                    ? 'No practice yet. Every set you finish — reading, listening, writing, or speaking — lands here.'
                    : `No ${skill} practice yet.`}
                </p>
              ) : (
                <div className="mt-5 border border-slate-200 bg-white">
                  {filteredPractice.map((row, i) => {
                    const Icon = SECTION_ICON[row.section] || BookOpen;
                    if (row.kind === 'attempt') {
                      return (
                        <div
                          key={row.id}
                          className={cn(
                            'flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-slate-50',
                            i > 0 && 'border-t border-slate-100'
                          )}
                          data-testid={`practice-row-${row.id}`}
                        >
                          <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-display text-[15px] font-semibold text-slate-900">{titleFor(row.task_type)}</p>
                            <p className="text-xs capitalize text-slate-400">{row.section} · {when(row.at)}</p>
                          </div>
                          <span className="text-sm tabular-nums text-slate-400">{row.n_correct} / {row.n_total}</span>
                          <span className={cn('h-display w-12 shrink-0 text-right text-lg tabular-nums', pctCls(row.pct))}>{row.pct}%</span>
                          <button
                            type="button"
                            data-testid={`attempt-review-${row.id}`}
                            onClick={() => navigate(`/${exam}/practice-attempts/${row.id}/review`)}
                            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                          >
                            Review <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    }
                    const status = statusMeta(row.status);
                    const zeroed = row.status === 'gate_zeroed';
                    const band = !zeroed && (row.feedback?.band_estimate || row.feedback?.clb_band_estimate);
                    return (
                      <div
                        key={row.id}
                        className={cn(
                          'flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-slate-50',
                          i > 0 && 'border-t border-slate-100'
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-display text-[15px] font-semibold text-slate-900">{titleFor(row.task_type)}</p>
                          <p className="text-xs capitalize text-slate-400">{row.section} · {when(row.at)}</p>
                        </div>
                        <span className="flex shrink-0 items-center gap-3">
                          {band && <span className="h-display text-lg text-slate-900">{band}</span>}
                          <span className={cn('rounded px-2 py-0.5 text-xs font-semibold', status.cls)}>{status.text}</span>
                          <button
                            type="button"
                            data-testid={`submission-review-${row.id}`}
                            onClick={() => navigate(`/${exam}/submissions/${row.id}/review`)}
                            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                          >
                            Review <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </AppShell>
  );
}
